import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ArticlePositionLayerData } from '@3d-scene/layer/creator/article-position-layer-data';
import { LayerElementCreatorFactory } from '@3d-scene/manager/layer-element-creator.factory';
import { SceneManager } from '@3d-scene/manager/scene.manager';
import { GlobalContainer } from '@3d-scene/model3d/global-container';
import { ProjectModel } from '@project/model/project.model';
import { Log } from '@shared/debug/log';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Group } from 'three';

const allLayerNames = ['dimensions', 'articleNumbers', 'bmk', 'drillingHoles', 'ruler', 'articlePosition'] as const;
export type LayerName = typeof allLayerNames[number];

export type LayerDataHandlers = {
  articlePosition: ArticlePositionLayerData,
}

@Injectable({providedIn: 'root'})
export class LayerManager {

  /* in order to exclude the layers from certain operations (e.g. rendering screenshots),
  we render them on a separate threejs layer */
  private static readonly LAYERS_THREEJS_LAYER_INDEX = 1;

  private _layerActiveState: Record<LayerName, boolean> = {
    dimensions: false,
    articleNumbers: false,
    bmk: false,
    drillingHoles: false,
    ruler: false,
    articlePosition: false,
  }

  // Holds Subscriptions for active layers.
  private _layerUpdates: Record<LayerName, Subscription | undefined> = {
    dimensions: undefined,
    articleNumbers: undefined,
    bmk: undefined,
    drillingHoles: undefined,
    ruler: undefined,
    articlePosition: undefined,
  }


  private layerObjectsParent: Group = new Group();

  private updateSubject = new Subject<void>();

  constructor(
    private globalContainer: GlobalContainer,
    private layerElementCreatorFactory: LayerElementCreatorFactory,
    private sceneManager: SceneManager,
    private localeResourceManager: LocaleResourceManager,
    private projectModel: ProjectModel,
  ) {
    this.localeResourceManager.localeChanged$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.queueUpdate());

    this.updateSubject
      .pipe(takeUntilDestroyed(), debounceTime(100))
      .subscribe(() => this.update());

    this.projectModel.projectChanged$.pipe(takeUntilDestroyed())
      .subscribe(() => {
        // when editing articles, the drillingholes view doesn't make sense, so we deactivate it on every projectChange
        this.deactivate('drillingHoles')
      })
  }

  public layerDataHandlers: LayerDataHandlers = {
    articlePosition: new ArticlePositionLayerData(),
  };

  /* map defines incompatibility in both directions. For example activating bmk will disable articleNumbers,
    * and activating articleNumbers will deactivate bmk */
  private incompatibleLayers = new Map<LayerName, LayerName[]>([
    ['bmk', ['articleNumbers']],
    ['drillingHoles', ['dimensions', 'articleNumbers', 'bmk', 'ruler', 'articlePosition']],
  ]);

  public isActive(layerName: LayerName): boolean {
    return this._layerActiveState[layerName];
  }

  public get layerActiveState(): Readonly<Record<LayerName, boolean>> {
    return this._layerActiveState;
  }

  public get activeLayers(): LayerName[] {
    return allLayerNames.filter((layerName) => this._layerActiveState[layerName]);
  }

  public activate(...layersToActivate: LayerName[]): void {
    const anyLayersChanged = layersToActivate.some((name) => !this._layerActiveState[name]);
    if (!anyLayersChanged) return;

    const layersToDeactivate = this.getIncompatibleLayers(layersToActivate);

    if (layersToActivate.some(layerName => layersToDeactivate.includes(layerName))) {
      Log.error(`cannot activate mutually exclusive layers ${layersToActivate}`);
      return;
    }

    this.activateLayers(layersToActivate);
    this.deactivateLayers(layersToDeactivate);

    this.update();
  }

  public deactivate(...layers: LayerName[]): void {
    const anyLayersChanged = layers.some((name) => this._layerActiveState[name]);
    if (!anyLayersChanged) return;

    this.deactivateLayers(layers);

    this.update();
  }

  public toggle(layer: LayerName): void {
    if (this._layerActiveState[layer]) {
      this.deactivate(layer);
    } else {
      this.activate(layer);
    }
  }

  public queueUpdate(): void {
    this.updateSubject.next();
  }

  public updateLayer(layerName: LayerName): void {
    if (!this.isActive(layerName)) {
      return;
    }

    this.removeLayerElements(layerName);

    const elements = this.layerElementCreatorFactory.getCreator(layerName, this.layerDataHandlers).createLayerElements();
    this.layerObjectsParent.add(elements);

    this.layerObjectsParent.traverse((child) => {
      child.layers.set(LayerManager.LAYERS_THREEJS_LAYER_INDEX);
    });

    this.globalContainer.add(this.layerObjectsParent);
    this.sceneManager.renderScene();
  }

  public hideStaticLayers(): void {
    this.deactivate('dimensions', 'drillingHoles');
  }

  public reset(): void {
    this.deactivate(...allLayerNames);
    this.update();
  }

  private update(): void {
    this.layerObjectsParent.clear();
    for (const layerName of allLayerNames) {
      if (this.isActive(layerName)) {
        const elements = this.layerElementCreatorFactory.getCreator(layerName, this.layerDataHandlers).createLayerElements();
        this.layerObjectsParent.add(elements);
      }
    }

    this.layerObjectsParent.traverse((child) => {
      child.layers.set(LayerManager.LAYERS_THREEJS_LAYER_INDEX);
    });

    this.globalContainer.add(this.layerObjectsParent);
    this.sceneManager.renderScene();
  }

  private startUpdatesForLayer(layerName: LayerName): void {
    if (this._layerUpdates[layerName]) {
      this.stopUpdatesForLayer(layerName);
    }
    this._layerUpdates[layerName] = this.layerElementCreatorFactory.subscribeToLayerUpdates(layerName, () => this.updateLayer(layerName));
  }

  private stopUpdatesForLayer(layerName: LayerName): void {
    this._layerUpdates[layerName]?.unsubscribe();
    this._layerUpdates[layerName] = undefined;
  }

  private activateLayers(layersToActivate: LayerName[]): void {
    for (const layer of layersToActivate) {
      this.layerElementCreatorFactory.getCreator(layer, this.layerDataHandlers).onActivate();
      this.startUpdatesForLayer(layer);
      this._layerActiveState[layer] = true;
    }
  }

  private deactivateLayers(layersToDeactivate: LayerName[]): void {
    for (const layer of layersToDeactivate) {
      this.stopUpdatesForLayer(layer);
      this.layerElementCreatorFactory.getCreator(layer, this.layerDataHandlers).onDeactivate();
      this._layerActiveState[layer] = false;
    }
  }

  /** determines the layers that should be deactivated because they are incompatible with a layer about to be activated */
  private getIncompatibleLayers(layersToActivate: LayerName[]): LayerName[] {
    const toDeactivate = layersToActivate.flatMap(
      (layer) => this.incompatibleLayers.get(layer) ?? []);

    for (const layerToActivate of layersToActivate) {
      for (const activeLayer of this.activeLayers) {
        if ((this.incompatibleLayers.get(activeLayer) ?? []).includes(layerToActivate)) {
          toDeactivate.push(activeLayer);
        }
      }
    }

    return toDeactivate;
  }

  private removeLayerElements(layer: string): void {
    this.layerObjectsParent.traverse((obj) => {
      if (obj.name === layer) {
        this.layerObjectsParent.remove(obj);
      }
    });
  }
}
